/* eslint-disable react/jsx-props-no-spreading */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import get from 'lodash/get';
import { Skeleton, Dropdown, Menu } from 'antd';
import moment from 'moment';
import { useHistory } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';

import { billingCyclesSelector } from 'data/selectors/billingCycles';
import PaymentButton from 'components/paymentGate/components/PaymentButtons';
import calculatePrice from 'helpers/calculatePrice';
import { Icon } from 'components/common';
import { discountCount } from 'helpers/calcHelpers';
import { toggleModal } from 'data/actions/modals';
import Loader from 'components/loading';
import { planDescriptionMap, planDescriptionMapSecondRow } from 'data/types/subscription.types';
import { purchaseStatusSelector, subscriptionsSelector } from 'data/selectors/subscriptions';
import { verificationsBulkSelector } from 'data/selectors/verifications';
import { sendViewItemAnalytic, sendAddToCartAnalytic } from 'helpers/googleAnalytics';
import { PAYERS } from 'constants/payers';
import { isBlackFridayPricingSelector, isUserGetProspectAdminSelector, priceIdKeySelector } from 'data/selectors/user';
import { phoneSearchProPlanSelector } from 'data/selectors/plans';
import { purchaseSubscription, updateStripeInfo } from 'data/actions/subscriptions';
import { purchaseSubscriptionLoaderSelector } from 'data/selectors/loading';
import { PURCHASE_STATUS } from 'constants/paymentGate';
import { toggleMessage } from 'data/actions/message';
import {
  FastSpringWrapper,
  BillingWrapper,
  BillingBtnWrapper,
  BillingHeader,
  PaymentDescriptionGreyLite,
  PeriodWrapper,
  PlanDescription,
  PlanDetails,
  BillingSubHeader,
  SummaryWrapper,
  SummaryRow,
  QuantityWrapper,
  QuantityCount,
  SummaryRowItem,
  SummaryTotal,
  PayerRadioGroup,
  PayerRadio,
  PayerWrapper,
  DropDownLabel,
} from '../styles';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);
const stripeTestPromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_TEST_KEY);

const StripeGate = ({
  billingCycle,
  plan,
  bulkId,
  phoneSearchPlan,
  phoneSearchQuantity,
  payDueTodayPhoneSearch,
  selectSubscriptionQuantity,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const subscription = useSelector(subscriptionsSelector);
  const bulk = useSelector(verificationsBulkSelector(bulkId));
  const billingCyclesData = useSelector(billingCyclesSelector);
  const isBlackFriday = useSelector(isBlackFridayPricingSelector);
  const phoneSearchProPlan = useSelector(phoneSearchProPlanSelector);
  const priceIdKey = useSelector(priceIdKeySelector);
  const purchaseStatus = useSelector(purchaseStatusSelector);
  const isProcessing = useSelector(purchaseSubscriptionLoaderSelector);
  const isAdmin = useSelector(isUserGetProspectAdminSelector);

  const [checkoutCycle, setCheckoutCycle] = useState(billingCycle);
  const [quantity, setQuantity] = useState(selectSubscriptionQuantity || 1);

  let emailsProduct;
  let price = 0;
  const period = get(checkoutCycle, 'period', 'month');
  if (plan) {
    const planPrice = calculatePrice({
      planPrice: plan.price,
      period,
      discount: get(checkoutCycle, 'discount'),
      isBlackFriday,
    });
    price += planPrice * quantity;
    const priceId = get(plan, 'prices', []).find((item) => item.price === planPrice)?.[priceIdKey];
    emailsProduct = {
      priceId,
      quantity: selectSubscriptionQuantity,
      coupon: subscription.coupon,
    };
  }
  let phonesProduct;
  if (payDueTodayPhoneSearch) {
    const priceId = get(phoneSearchProPlan, 'prices', []).find((item) =>
      period === 'year' ? item.isYearly : item.default,
    )?.[priceIdKey];
    phonesProduct = {
      priceId,
      quantity: phoneSearchQuantity,
    };
  }

  const [payer, setPayer] = useState(subscription?.payer || PAYERS.INDIVIDUAL);

  const pauseDiscount = useMemo(() => {
    if (
      !bulkId &&
      subscription?.status === 'pause' &&
      moment().diff(subscription?.pauseData?.pauseSetAt, 'month') >= 1
    ) {
      return 30;
    }
    return 0;
  }, [subscription, bulkId]);

  const checkout = () => {
    sendAddToCartAnalytic(plan, checkoutCycle, quantity);
    dispatch(
      purchaseSubscription({
        emailsProduct,
        phonesProduct,
        onSuccess: async (data) => {
          if (data?.clientSecret && data?.paymentMethodId) {
            const stripe = isAdmin ? await stripeTestPromise : await stripePromise;
            const result = await stripe.confirmCardPayment(data.clientSecret, {
              payment_method: data.paymentMethodId,
            });
            if (result.error) {
              dispatch(toggleMessage('error', { text: result.error.message }));
            }
          } else if (data?.url) {
            window.location.href = data.url;
          } else {
            history.push('/settings/subscriptions');
          }
          dispatch(toggleModal('billing', false));
        },
      }),
    );
  };

  const addCard = () => {
    dispatch(
      updateStripeInfo((data) => {
        window.open(data.url, '_blank');
      }),
    );
  };

  useEffect(() => {
    sendViewItemAnalytic({
      plan,
      price,
      billingCycle,
      quantity,
    });
  }, [price]); // eslint-disable-line

  const dropdownItems = useCallback(
    () =>
      billingCyclesData.map((cycle) => (
        <Menu.Item
          key={cycle?.name}
          onClick={() => {
            if (isProcessing) return;
            const newCurrentBilling = billingCyclesData.find((cycleItem) => cycleItem._id === cycle._id);
            setCheckoutCycle(newCurrentBilling);
          }}
        >
          {cycle?.name}
        </Menu.Item>
      )),
    [billingCyclesData, isProcessing],
  );

  const payerData = () => (
    <PayerWrapper>
      <BillingHeader style={{ marginBottom: 16 }}>Payer</BillingHeader>
      <PayerRadioGroup
        onChange={(payerType) => {
          setPayer(get(payerType, 'target.value', PAYERS.INDIVIDUAL));
        }}
        value={payer}
      >
        <PayerRadio size="default" value={PAYERS.INDIVIDUAL}>
          Individual
        </PayerRadio>
        <PayerRadio size="default" value={PAYERS.COMPANY}>
          Company
        </PayerRadio>
      </PayerRadioGroup>
    </PayerWrapper>
  );

  const summaryData = () => {
    const isYearly = checkoutCycle.period === 'year';
    const planCost = (isYearly ? plan?.price * 12 * quantity : plan?.price * quantity) || 0;
    let phoneSearchConst = phoneSearchPlan?.price || 0;
    if (payDueTodayPhoneSearch) phoneSearchConst = payDueTodayPhoneSearch < 0 ? 0 : payDueTodayPhoneSearch;
    let subtotal = planCost;
    if (phoneSearchPlan?.price && (!payDueTodayPhoneSearch || payDueTodayPhoneSearch > 0)) {
      subtotal += payDueTodayPhoneSearch || phoneSearchPlan?.price || 0;
    }
    const planPrice = purchaseStatus === PURCHASE_STATUS.CREATE_SUBSCRIPTION_SESSION ? price + phoneSearchConst : price;
    let finalPrice = calculatePrice({
      planPrice,
      discount: subscription?.discountPercent,
    });
    if (purchaseStatus !== PURCHASE_STATUS.CREATE_SUBSCRIPTION_SESSION && phoneSearchConst > 0) {
      finalPrice += phoneSearchConst;
    }
    let discount;
    if (planCost) {
      discount = discountCount({
        price: planCost,
        discount: pauseDiscount || checkoutCycle.discount,
        isBlackFriday,
      });
      if (subscription?.discountPercent) {
        discount += discountCount({
          price: planCost - discount + phoneSearchConst,
          discount: subscription?.discountPercent,
          isBlackFriday,
        });
      }
    }
    const skeletonOptions = {
      loading: isProcessing,
      paragraph: false,
      active: true,
    };

    return (
      <SummaryWrapper>
        <BillingHeader>Summary</BillingHeader>
        {['Pro', 'Growth 50K'].includes(plan?.name) ? (
          <SummaryRow>
            <SummaryRowItem lite>Plan Quantity</SummaryRowItem>
            <SummaryRowItem>
              <QuantityWrapper>
                <Icon
                  type="ic-remove"
                  fill="grey"
                  onClick={() => {
                    if (quantity === 1 || isProcessing) return;
                    const newQuantity = quantity - 1;
                    setQuantity(newQuantity);
                  }}
                />
                <QuantityCount>{quantity}</QuantityCount>
                <Icon
                  type="ic-add"
                  fill="grey"
                  onClick={() => {
                    if (quantity >= 10 || isProcessing) return;
                    const newQuantity = quantity + 1;
                    setQuantity(newQuantity);
                  }}
                />
              </QuantityWrapper>
            </SummaryRowItem>
          </SummaryRow>
        ) : null}
        <SummaryRow>
          {plan && !plan.isCheapest && (
            <>
              <SummaryRowItem lite>Period</SummaryRowItem>
              <Dropdown trigger={['click']} overlay={<Menu>{dropdownItems()}</Menu>}>
                <DropDownLabel>
                  {checkoutCycle?.name}
                  <Icon type="ic-arrow-drop-down" />
                </DropDownLabel>
              </Dropdown>
            </>
          )}
        </SummaryRow>
        <SummaryRow>
          {plan && (
            <>
              <SummaryRowItem lite>Plan cost</SummaryRowItem>
              <SummaryRowItem>{`$${planCost?.toFixed(0)} USD`}</SummaryRowItem>
            </>
          )}
        </SummaryRow>
        {phoneSearchPlan && (
          <SummaryRow>
            <SummaryRowItem lite>Phone number credits</SummaryRowItem>
            <SummaryRowItem>{`$${phoneSearchConst.toFixed(0)} USD`}</SummaryRowItem>
          </SummaryRow>
        )}
        <SummaryRow>
          <SummaryRowItem lite>Subtotal</SummaryRowItem>
          <SummaryRowItem>
            <Skeleton {...skeletonOptions}>{`$${subtotal?.toFixed(0)} USD`}</Skeleton>
          </SummaryRowItem>
        </SummaryRow>
        {discount ? (
          <SummaryRow>
            <SummaryRowItem lite>Discount</SummaryRowItem>
            <SummaryRowItem>
              <Skeleton {...skeletonOptions}>{`- $${discount?.toFixed(0)} USD`}</Skeleton>
            </SummaryRowItem>
          </SummaryRow>
        ) : null}
        <SummaryTotal>
          <SummaryRowItem>Total</SummaryRowItem>
          <SummaryRowItem>
            <Skeleton {...skeletonOptions}>{`$${finalPrice?.toFixed(0)} USD`}</Skeleton>
          </SummaryRowItem>
        </SummaryTotal>
      </SummaryWrapper>
    );
  };

  const bulkSummaryData = () => {
    const finalPrice = price;
    const skeletonOptions = {
      loading: isProcessing,
      paragraph: false,
      active: true,
    };

    return (
      <SummaryWrapper>
        <BillingHeader>Summary</BillingHeader>
        <SummaryTotal>
          <SummaryRowItem>Total</SummaryRowItem>
          <SummaryRowItem>
            <Skeleton {...skeletonOptions}>{`$${finalPrice?.toFixed(2)} USD`}</Skeleton>
          </SummaryRowItem>
        </SummaryTotal>
      </SummaryWrapper>
    );
  };

  return (
    <Loader loading={isProcessing}>
      <FastSpringWrapper>
        <BillingWrapper>
          <PeriodWrapper>
            {!bulkId && plan ? (
              <>
                <BillingHeader>Plan and period</BillingHeader>
                <PlanDescription>
                  <PlanDetails>
                    <BillingSubHeader>{plan?.name} plan</BillingSubHeader>
                    {planDescriptionMap.map((item) =>
                      plan[item.key] ? (
                        <PaymentDescriptionGreyLite key={item.key}>
                          {`${parseInt(quantity * plan[item.key] * (billingCycle?.period === 'year' ? 12 : 1), 10)
                            ?.toLocaleString()
                            ?.replace(/,/g, ' ')} ${item.label}`}
                        </PaymentDescriptionGreyLite>
                      ) : null,
                    )}
                  </PlanDetails>
                  <PlanDetails style={{ marginTop: '26px' }}>
                    {planDescriptionMapSecondRow.map((item) =>
                      plan[item.key] ? (
                        <PaymentDescriptionGreyLite key={item.key}>
                          {`${parseInt(quantity * plan[item.key], 10)
                            ?.toLocaleString()
                            ?.replace(/,/g, ' ')} ${item.label}`}
                        </PaymentDescriptionGreyLite>
                      ) : null,
                    )}
                    {phoneSearchPlan?.credits > 0 && (
                      <PaymentDescriptionGreyLite>
                        {`${parseInt(phoneSearchPlan?.credits * (billingCycle?.period === 'year' ? 12 : 1), 10)
                          ?.toLocaleString()
                          ?.replace(/,/g, ' ')} phone number credits`}
                      </PaymentDescriptionGreyLite>
                    )}
                  </PlanDetails>
                </PlanDescription>
              </>
            ) : null}
            {bulk ? bulkSummaryData() : summaryData()}
            {!subscription?.payer ? payerData() : null}
          </PeriodWrapper>
        </BillingWrapper>
        <BillingBtnWrapper>
          <PaymentButton addCard={addCard} canAddCard isProcessing={isProcessing} checkout={checkout} />
        </BillingBtnWrapper>
      </FastSpringWrapper>
    </Loader>
  );
};

export default StripeGate;
